import {
  CURRENCY,
  RESEARCH,
  RESERVE,
  TECHS,
  TECH_IDS,
  type BuildingId,
  type TechId,
  type TradeGoodId,
} from '@sd/content';
import type { PlanetState } from './planet';

/**
 * Technologies and research (GDD §10). `techMods` folds what a player knows
 * into one set of modifiers the rest of the simulation reads; the research
 * helpers price technologies and share out the science stock. Pure.
 */
export interface TechMods {
  readonly jobsPerLevel: Readonly<Partial<Record<BuildingId, number>>>;
  readonly buildingOutput: Readonly<Partial<Record<BuildingId, number>>>;
  readonly buildingInputs: Readonly<
    Partial<Record<BuildingId, Readonly<Partial<Record<TradeGoodId, number>>>>>
  >;
  readonly heliumPerEnergy: number;
  readonly orbitSlots: number;
  readonly surfaceSlots: number;
  readonly populationGrowth: number;
  readonly outpostOutput: number;
  readonly exoticsYield: number;
  readonly contractSlots: number;
  readonly caravanSpeed: number;
  readonly conversionFee: number;
  readonly seigniorage: number;
  readonly scanTime: number;
  readonly fleetUpkeep: number;
  readonly fleetLimit: number;
  readonly crisisWarningTicks: number;
  readonly crisisDamage: number;
  readonly projectPrestige: number;
  readonly megastructure: boolean;
  readonly revealMap: boolean;
  readonly autoTrade: boolean;
  readonly formations: boolean;
}

export const NO_TECH: TechMods = {
  jobsPerLevel: {},
  buildingOutput: {},
  buildingInputs: {},
  heliumPerEnergy: 1,
  orbitSlots: 0,
  surfaceSlots: 0,
  populationGrowth: 0,
  outpostOutput: 0,
  exoticsYield: 1,
  contractSlots: 0,
  caravanSpeed: 0,
  conversionFee: CURRENCY.conversionFee,
  seigniorage: RESERVE.seigniorage,
  scanTime: 1,
  fleetUpkeep: 1,
  fleetLimit: 0,
  crisisWarningTicks: 0,
  crisisDamage: 1,
  projectPrestige: 0,
  megastructure: false,
  revealMap: false,
  autoTrade: false,
  formations: false,
};

export const isTech = (value: string): value is TechId =>
  (TECH_IDS as readonly string[]).includes(value);

// States share their tech lists between ticks, so most lookups hit the first cache.
const byList = new WeakMap<readonly TechId[], TechMods>();
const byKey = new Map<string, TechMods>();

/** Every technology's effects combined: bonuses add up, factors multiply, fees take the best. */
export function techMods(techs: readonly TechId[]): TechMods {
  if (techs.length === 0) return NO_TECH;
  const seen = byList.get(techs);
  if (seen) return seen;
  const key = [...techs].sort().join(',');
  const hit = byKey.get(key);
  if (hit) {
    byList.set(techs, hit);
    return hit;
  }
  const mods = combine(techs);
  byKey.set(key, mods);
  byList.set(techs, mods);
  return mods;
}

function combine(techs: readonly TechId[]): TechMods {
  const jobsPerLevel: Partial<Record<BuildingId, number>> = {};
  const buildingOutput: Partial<Record<BuildingId, number>> = {};
  const buildingInputs: Partial<Record<BuildingId, Partial<Record<TradeGoodId, number>>>> = {};
  const m: { -readonly [K in keyof TechMods]: TechMods[K] } = { ...NO_TECH };
  for (const id of techs) {
    const e = TECHS[id].effects;
    for (const [b, n] of Object.entries(e.jobsPerLevel ?? {}) as [BuildingId, number][]) {
      jobsPerLevel[b] = (jobsPerLevel[b] ?? 0) + n;
    }
    for (const [b, n] of Object.entries(e.buildingOutput ?? {}) as [BuildingId, number][]) {
      buildingOutput[b] = (buildingOutput[b] ?? 0) + n;
    }
    for (const [b, goods] of Object.entries(e.buildingInputs ?? {}) as [
      BuildingId,
      Partial<Record<TradeGoodId, number>>,
    ][]) {
      const into = (buildingInputs[b] ??= {});
      for (const [g, f] of Object.entries(goods) as [TradeGoodId, number][]) {
        into[g] = (into[g] ?? 1) * f;
      }
    }
    m.heliumPerEnergy *= e.heliumPerEnergy ?? 1;
    m.orbitSlots += e.orbitSlots ?? 0;
    m.surfaceSlots += e.surfaceSlots ?? 0;
    m.populationGrowth += e.populationGrowth ?? 0;
    m.outpostOutput += e.outpostOutput ?? 0;
    m.exoticsYield *= e.exoticsYield ?? 1;
    m.contractSlots += e.contractSlots ?? 0;
    m.caravanSpeed += e.caravanSpeed ?? 0;
    m.conversionFee = Math.min(m.conversionFee, e.conversionFee ?? Infinity);
    m.seigniorage = Math.max(m.seigniorage, e.seigniorage ?? 0);
    m.scanTime *= e.scanTime ?? 1;
    m.fleetUpkeep *= e.fleetUpkeep ?? 1;
    m.fleetLimit += e.fleetLimit ?? 0;
    m.crisisWarningTicks += e.crisisWarningTicks ?? 0;
    m.crisisDamage *= e.crisisDamage ?? 1;
    m.projectPrestige += e.projectPrestige ?? 0;
    m.megastructure ||= e.megastructure ?? false;
    m.revealMap ||= e.revealMap ?? false;
    m.autoTrade ||= e.autoTrade ?? false;
    m.formations ||= e.formations ?? false;
  }
  return { ...m, jobsPerLevel, buildingOutput, buildingInputs };
}

/** A player's research: what runs now, what is already in, and fragment discounts. */
export interface ResearchState {
  /** Technologies being researched, in the order they were started. */
  readonly active: readonly TechId[];
  /** Science already put into each technology; kept when research is cancelled. */
  readonly progress: Readonly<Partial<Record<TechId, number>>>;
  /** Technologies whose exotics are paid (on the first start; never twice). */
  readonly exoticsPaid: readonly TechId[];
  /** Share off the science price from technology fragments (GDD §7). */
  readonly discounts: Readonly<Partial<Record<TechId, number>>>;
}

export const createResearch = (): ResearchState => ({
  active: [],
  progress: {},
  exoticsPaid: [],
  discounts: {},
});

/** How many technologies a capital researches at once. */
export function researchSlots(planet: PlanetState): number {
  const level = Math.max(
    0,
    ...planet.buildings.filter((b) => b.id === 'researchCenter').map((b) => b.level),
  );
  return level >= 2 ? RESEARCH.parallelWithResearchLevel2 : RESEARCH.parallel;
}

/**
 * Science price of a technology now: × the diffusion factor for every other
 * player who knows it, less the fragment discount.
 */
export function techPrice(tech: TechId, othersKnowing: number, discount = 0): number {
  const base = RESEARCH.cost[TECHS[tech].tier].science;
  const price = base * RESEARCH.diffusionFactor ** othersKnowing * (1 - discount);
  return Math.round(price * 100) / 100;
}

/** Why `tech` cannot be researched by someone who knows `known`, or null. Ignores slots and cost. */
export function researchBlocker(tech: TechId, known: readonly TechId[]): string | null {
  if (known.includes(tech)) return 'already known';
  const def = TECHS[tech];
  const met = def.requires.filter((r) => known.includes(r)).length;
  return met >= def.requiresCount ? null : 'requirements not met';
}

/**
 * Shares a science stock between technologies, each needing `needs[i]`:
 * equal shares, and what one does not need goes to the others.
 */
export function shareScience(stock: number, needs: readonly number[]): number[] {
  const given = needs.map(() => 0);
  const order = needs.map((_, i) => i).sort((a, b) => needs[a]! - needs[b]! || a - b);
  let left = stock;
  order.forEach((i, k) => {
    const share = Math.min(needs[i]!, left / (order.length - k));
    given[i] = share;
    left -= share;
  });
  return given;
}

/** Technologies a fragment may discount: unknown, tier 1–2, below the cap. */
export function fragmentTargets(research: ResearchState, known: readonly TechId[]): TechId[] {
  return TECH_IDS.filter(
    (id) =>
      (TECHS[id].tier === 1 || TECHS[id].tier === 2) &&
      !known.includes(id) &&
      (research.discounts[id] ?? 0) < RESEARCH.maxFragmentDiscount - 1e-9,
  );
}
